import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { paragon } from '@useparagon/connect';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  getDestinations,
  getDestinationActions,
  getActionConfigFields,
  reconnectParagonIntegration,
} from '@/services/workflowApi';
import useParagonAuth from '@/hooks/useParagonAuth';
import type {
  Integration,
  Action,
  ActionConfigField,
  DestinationActionSettings,
} from '@/types/workflow';
import { useMixpanel } from '@/hooks/useMixpanel';
import {
  SELECTED_AN_INTEGRATION,
  CLICKED_CONNECT_AN_INTEGRATION,
  CONNECTED_AN_INTEGRATION,
} from '@/analytics/mixpanel/constants/events';
import {
  INTEGRATION_NAME,
  IS_NEW_CONNECTION,
  CONNECTION_METHOD,
} from '@/analytics/mixpanel/constants/properties';

// Select option value that opens the "Connect an integration" side panel.
const CONNECT_PANEL_VALUE = '__connect_panel__';

/** Treat as connected only when status indicates a valid connection (align with Leadpages dashboard). */
const DISCONNECTED_STATUSES = ['error', 'inactive', 'disconnected', 'revoked'];

function isConnected(integration: Integration): boolean {
  const s = integration.status?.toLowerCase() ?? '';
  return !!s && !DISCONNECTED_STATUSES.includes(s);
}

// ─── Config field renderers ───────────────────────────────────────────────────

interface ConfigFieldProps {
  field: ActionConfigField;
  value: string | string[] | undefined;
  onChange: (value: string | string[]) => void;
}

const SelectConfigField: React.FC<ConfigFieldProps> = ({ field, value, onChange }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[11px] font-medium text-neutral-600">
      {field.label}
      {field.required && <span className="ml-0.5 text-red-500">*</span>}
    </label>
    <Select
      key={`${field.name}-${String(value)}`}
      value={(value as string) ?? ''}
      onValueChange={onChange}
    >
      <SelectTrigger className="w-full" data-testid={`select-field-${field.name}`}>
        <SelectValue placeholder={`Select ${field.label.toLowerCase()}…`} />
      </SelectTrigger>
      <SelectContent truncate={false}>
        {(field.options ?? []).map((opt) => (
          <SelectItem key={opt.value} value={opt.value} truncate={false}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const MultiSelectConfigField: React.FC<ConfigFieldProps> = ({ field, value, onChange }) => {
  const selected = Array.isArray(value) ? value : [];
  const options = field.options ?? [];
  const selectedOptions = options.filter((opt) => selected.includes(opt.value));
  const availableOptions = options.filter((opt) => !selected.includes(opt.value));

  const addItem = (optValue: string) => {
    onChange([...selected, optValue]);
  };

  const removeItem = (optValue: string) => {
    onChange(selected.filter((v) => v !== optValue));
  };

  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-medium text-neutral-600">
        {field.label}
        {field.required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      {/*
        WHY: SelectTrigger IS the outer chip container. Radix measures the trigger element
        to set --radix-select-trigger-width, so making the trigger the full-width container
        means the dropdown naturally aligns and matches the container width — no manual
        measurement or minWidth state needed.
      */}
      <Select key={selected.join(',')} onValueChange={addItem}>
        <SelectTrigger
          hideIcon
          className="!h-auto min-h-[34px] !flex-wrap !items-start !gap-1 !rounded-lg border !border-neutral-200 !bg-white !px-2 !py-1.5 !ring-0 data-[state=open]:!border-violet-400 data-[state=open]:!ring-1 data-[state=open]:!ring-violet-400/30 data-[state=open]:!outline-none"
          data-testid={`multiselect-trigger-${field.name}`}
        >
          {selectedOptions.map((opt) => (
            <span
              key={opt.value}
              className="flex items-center gap-0.5 rounded bg-neutral-100 px-1.5 py-[3px] text-[11px] font-medium text-neutral-700"
            >
              {opt.label}
              {/*
                WHY: Must use <span role="button">, NOT <button>. The SelectTrigger is already a
                <button> element — nested <button> elements are invalid HTML and browsers silently
                restructure the DOM, breaking both event propagation and Radix's trigger width
                measurement (which controls the dropdown width).
              */}
              <span
                role="button"
                tabIndex={0}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  removeItem(opt.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    removeItem(opt.value);
                  }
                }}
                className="ml-0.5 cursor-pointer text-[13px] leading-none text-neutral-400 hover:text-neutral-700"
                aria-label={`Remove ${opt.label}`}
                data-testid={`remove-chip-${field.name}-${opt.value}`}
              >
                ×
              </span>
            </span>
          ))}
          <span className="flex-1 text-[12px] text-neutral-400" style={{ minWidth: '80px' }}>
            {availableOptions.length > 0
              ? `Add ${field.label.toLowerCase()}…`
              : selected.length === 0
                ? `No options available`
                : null}
          </span>
        </SelectTrigger>
        {/* truncate=true caps the dropdown to maxWidth=trigger width; items use truncate=false so long names wrap */}
        <SelectContent className="!max-h-[160px]">
          {availableOptions.map((opt) => (
            <SelectItem key={opt.value} value={opt.value} truncate={false}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

const TextConfigField: React.FC<ConfigFieldProps> = ({ field, value, onChange }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[11px] font-medium text-neutral-600">
      {field.label}
      {field.required && <span className="ml-0.5 text-red-500">*</span>}
    </label>
    <input
      type="text"
      value={(value as string) ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.label}
      className="h-8 w-full rounded-lg bg-neutral-50 px-2 text-[13px] text-neutral-900 ring-1 ring-neutral-200 outline-none focus:ring-indigo-500"
      data-testid={`text-field-${field.name}`}
    />
  </div>
);

const EmailConfigField: React.FC<ConfigFieldProps> = ({ field, value, onChange }) => {
  const emails = Array.isArray(value) ? value : value ? [value as string] : [];
  const [inputValue, setInputValue] = React.useState('');

  const addEmail = (email: string) => {
    const trimmed = email.trim();
    if (trimmed && !emails.includes(trimmed)) {
      onChange([...emails, trimmed]);
    }
    setInputValue('');
  };

  const removeEmail = (email: string) => {
    onChange(emails.filter((e) => e !== email));
  };

  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-medium text-neutral-600">
        {field.label}
        {field.required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      <div className="flex min-h-[34px] flex-wrap gap-1 rounded-lg border border-neutral-200 bg-white px-2 py-1.5">
        {emails.map((email) => (
          <span
            key={email}
            className="flex items-center gap-0.5 rounded bg-neutral-100 px-1.5 py-[3px] text-[11px] font-medium text-neutral-700"
          >
            {email}
            <span
              role="button"
              tabIndex={0}
              onClick={() => removeEmail(email)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') removeEmail(email);
              }}
              className="ml-0.5 cursor-pointer text-[13px] leading-none text-neutral-400 hover:text-neutral-700"
              aria-label={`Remove ${email}`}
              data-testid={`remove-email-${field.name}-${email}`}
            >
              ×
            </span>
          </span>
        ))}
        <input
          type="email"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              addEmail(inputValue);
            }
          }}
          onBlur={() => {
            if (inputValue.trim()) addEmail(inputValue);
          }}
          placeholder={emails.length === 0 ? 'Add email address…' : ''}
          className="min-w-[120px] flex-1 bg-transparent text-[12px] text-neutral-900 outline-none placeholder:text-neutral-400"
          data-testid={`email-field-${field.name}`}
        />
      </div>
    </div>
  );
};

// WHY: Dispatch on field.type, mirroring the main app's ActionConfig.tsx.
// 'select' → single string value; 'tag-select' / 'multi-select' → string[] value.
// Using field.type (not maxSelected) avoids sending array values for single-select
// parent fields like google_ads_account, which turbine expects as plain strings.
const ConfigField: React.FC<ConfigFieldProps> = ({ field, value, onChange }) => {
  switch (field.type) {
    case 'select':
      return <SelectConfigField field={field} value={value} onChange={onChange} />;
    case 'tag-select':
    case 'multi-select':
      return <MultiSelectConfigField field={field} value={value} onChange={onChange} />;
    case 'email':
      return <EmailConfigField field={field} value={value} onChange={onChange} />;
    default:
      return <TextConfigField field={field} value={value} onChange={onChange} />;
  }
};

// ─── Spinner ─────────────────────────────────────────────────────────────────

const Spinner: React.FC = () => (
  <div className="flex justify-center py-4">
    <div className="h-5 w-5 animate-spin rounded-full border-2 border-violet-200 border-t-violet-600" />
  </div>
);

// ─── API error box ────────────────────────────────────────────────────────────

const ApiErrorBox: React.FC<{ message: string }> = ({ message }) => (
  <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5" role="alert">
    <p className="text-[11px] leading-relaxed text-red-600">{message}</p>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

export interface DestinationStepProps {
  onComplete: (settings: DestinationActionSettings, supportsFieldMapping: boolean) => void;
  initialSettings?: DestinationActionSettings;
  /** Fired immediately when the API reveals whether field mapping is supported, so the parent
   *  can update the step indicator without waiting for the Continue button click. */
  onSupportsFieldMappingChange?: (value: boolean) => void;
}

export const DestinationStep: React.FC<DestinationStepProps> = ({
  onComplete,
  initialSettings,
  onSupportsFieldMappingChange,
}) => {
  const { isAuthenticated, error: paragonAuthError } = useParagonAuth();
  const track = useMixpanel();

  const [paragonIntegrations, setParagonIntegrations] = useState<{ type: string; name: string }[]>(
    [],
  );

  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [integrationsError, setIntegrationsError] = useState<string | null>(null);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);

  const [actions, setActions] = useState<Action[]>([]);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [actionsError, setActionsError] = useState<string | null>(null);
  const [selectedAction, setSelectedAction] = useState<Action | null>(null);

  const [configFields, setConfigFields] = useState<ActionConfigField[]>([]);
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [config, setConfig] = useState<Record<string, string | string[]>>({});
  const [fieldDependencies, setFieldDependencies] = useState<Record<string, string[]>>({});
  // Default true: safe fallback so field mapping is shown if the API omits the flag.
  const [supportsFieldMapping, setSupportsFieldMapping] = useState(true);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [showConnectPanel, setShowConnectPanel] = useState(false);
  const initialSettingsRef = React.useRef<DestinationActionSettings | undefined>(initialSettings);
  const initialFieldMappingRef = React.useRef<Record<string, string>>(
    initialSettings?.fieldMapping ?? {},
  );

  React.useEffect(() => {
    initialFieldMappingRef.current = initialSettings?.fieldMapping ?? {};
  }, [initialSettings?.fieldMapping]);

  // ── Loaders ──────────────────────────────────────────────────────────────

  const loadIntegrations = useCallback(async (autoSelectCredentialId?: string) => {
    setIntegrationsLoading(true);
    setIntegrationsError(null);
    try {
      const response = await getDestinations();
      if (response._status.code >= 400) throw new Error(`${response._status.code}`);
      const items = response._items ?? [];
      const connected = items.filter(isConnected);
      const notConnected = items.filter((i) => !isConnected(i));
      setIntegrations([...connected, ...notConnected]);
      if (autoSelectCredentialId) {
        const newIntegration = connected.find((i) => i.credentialId === autoSelectCredentialId);
        if (newIntegration) {
          // WHY: Reset dependent state in the same batch so the config-fields effect never
          // fires with a stale action from the previously selected integration. Without this,
          // the effect sees newIntegration + old selectedAction and immediately calls
          // getActionConfigFields with a mismatched pair, producing a spurious error.
          setSelectedAction(null);
          setActions([]);
          setConfigFields([]);
          setConfig({});
          setSelectedIntegration(newIntegration);
        }
      } else if (initialSettingsRef.current) {
        const match = items.find(
          (i) => i.credentialId === initialSettingsRef.current!.credentialId,
        );
        if (match && isConnected(match)) {
          setSelectedIntegration(match);
        } else {
          setSelectedIntegration(null);
          setSelectedAction(null);
          setConfig({});
          initialSettingsRef.current = undefined;
          initialFieldMappingRef.current = {};
        }
      }
    } catch {
      setIntegrations([]);
      setIntegrationsError('Failed to load integrations. Please try again.');
    } finally {
      setIntegrationsLoading(false);
    }
  }, []);

  const loadActions = useCallback(async (integrationType: string) => {
    setActionsLoading(true);
    setActionsError(null);
    try {
      const response = await getDestinationActions(integrationType);
      if (response._status.code >= 400) throw new Error(`${response._status.code}`);
      const items = response._items ?? [];
      setActions(items);
      if (initialSettingsRef.current) {
        const savedActionId = initialSettingsRef.current.actionId;
        // Some automations from the main app store actionId as the action key
        // (e.g. "add_to_list") rather than the numeric/uuid id. Try both to handle legacy data.
        const match = items.find((a) => a.id === savedActionId || a.key === savedActionId);
        if (match) setSelectedAction(match);
      }
    } catch {
      setActions([]);
      setActionsError('Failed to load actions for this integration. Please try again.');
    } finally {
      setActionsLoading(false);
    }
  }, []);

  const loadConfigFields = useCallback(
    async (
      currentConfig: Record<string, string | string[]>,
      integration: Integration,
      action: Action,
      // WHY: append=true when a parent field changes — new dependent fields are added below
      // the existing ones rather than replacing them, so the user keeps seeing the parent
      // field they already selected (mirrors main app ActionConfig.tsx behaviour).
      append = false,
    ) => {
      setConfigLoading(true);
      setConfigError(null);
      try {
        const response = await getActionConfigFields({
          action: action.key,
          credentialId: integration.credentialId,
          configs: currentConfig,
        });
        if (response._status.code >= 400) throw new Error(`${response._status.code}`);
        const newFields = response.fields ?? [];
        setConfigFields((prev) => (append ? [...prev, ...newFields] : newFields));
        setFieldDependencies(response.fieldDependencies ?? {});
        setSupportsFieldMapping(response.supportsFieldMapping ?? true);
        if (initialSettingsRef.current) {
          setConfig(initialSettingsRef.current.config);
          initialSettingsRef.current = undefined;
        }
      } catch {
        setConfigFields([]);
        setConfigError('Failed to load configuration options. Please try again.');
      } finally {
        setConfigLoading(false);
      }
    },
    [],
  );

  // ── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => {
    void loadIntegrations();
  }, [loadIntegrations]);

  // WHY: getIntegrationMetadata() is only populated after paragon.authenticate() resolves.
  // Calling it before auth returns an empty array. We wait for isAuthenticated to flip true.
  useEffect(() => {
    if (!isAuthenticated) return;
    const metadata = paragon.getIntegrationMetadata() as { type: string; name: string }[];
    setParagonIntegrations(metadata);
  }, [isAuthenticated]);

  useEffect(() => {
    onSupportsFieldMappingChange?.(supportsFieldMapping);
  }, [supportsFieldMapping, onSupportsFieldMappingChange]);

  const lastAutoFetchKey = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedIntegration || !selectedAction || configLoading) return;
    const fetchedNames = new Set(configFields.map((f) => f.name));
    const needsFetch = Object.entries(fieldDependencies).some(
      ([dep, parents]) =>
        !fetchedNames.has(dep) && parents.length > 0 && parents.every((p) => !!config[p]),
    );
    if (!needsFetch) {
      lastAutoFetchKey.current = null;
      return;
    }
    const filteredConfig = Object.fromEntries(
      Object.entries(config).filter(([key]) => fetchedNames.has(key)),
    );
    const fetchKey = JSON.stringify(filteredConfig);
    if (fetchKey === lastAutoFetchKey.current) return;
    lastAutoFetchKey.current = fetchKey;
    void loadConfigFields(filteredConfig, selectedIntegration, selectedAction, true);
  }, [
    config,
    configFields,
    fieldDependencies,
    selectedIntegration,
    selectedAction,
    configLoading,
    loadConfigFields,
  ]);

  useEffect(() => {
    if (!selectedIntegration) {
      setActions([]);
      setSelectedAction(null);
      setConfigFields([]);
      setConfig({});
      return;
    }
    void loadActions(selectedIntegration.type);
  }, [selectedIntegration, loadActions]);

  useEffect(() => {
    if (!selectedIntegration || !selectedAction) {
      setConfigFields([]);
      setConfig({});
      return;
    }
    void loadConfigFields({}, selectedIntegration, selectedAction);
  }, [selectedAction, selectedIntegration, loadConfigFields]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleIntegrationChange = (value: string) => {
    if (value === CONNECT_PANEL_VALUE) {
      track(CLICKED_CONNECT_AN_INTEGRATION);
      setConnectError(null);
      setShowConnectPanel(true);
      return;
    }
    initialSettingsRef.current = undefined;
    initialFieldMappingRef.current = {};
    const integration = integrations.find((i) => i.credentialId === value) ?? null;
    if (integration) {
      track(SELECTED_AN_INTEGRATION, { [INTEGRATION_NAME]: integration.name });
    }
    setSelectedIntegration(integration);
    setSelectedAction(null);
    setActionsError(null);
    setConfigError(null);
    setConnectError(null);
    setSupportsFieldMapping(true);
  };

  const handleConnectIntegration = (providerType: string) => {
    const providerName =
      notConnectedIntegrations.find((i) => i.type === providerType)?.name ?? providerType;
    setConnectError(null);
    paragon.connect(providerType, {
      onSuccess: async (event: { credentialId?: string }) => {
        setShowConnectPanel(false);
        if (event.credentialId) {
          try {
            await reconnectParagonIntegration(event.credentialId);
          } catch {
            // Non-blocking: the new integration will still appear in the refreshed list.
          }
        }
        track(CONNECTED_AN_INTEGRATION, {
          [INTEGRATION_NAME]: providerName,
          [IS_NEW_CONNECTION]: true,
          [CONNECTION_METHOD]: 'modal',
        });
        await loadIntegrations(event.credentialId);
      },
      onError: () => {
        setConnectError('Failed to connect integration. Please try again.');
      },
    });
  };

  const handleActionChange = (value: string) => {
    initialSettingsRef.current = undefined;
    initialFieldMappingRef.current = {};
    const action = actions.find((a) => a.key === value) ?? null;
    setSelectedAction(action);
    setConfigError(null);
    setSupportsFieldMapping(true);
  };

  // WHY: Reverse map of fieldDependencies so we can look up "what depends on X"
  // in O(1) rather than scanning all entries on every change.
  const reverseFieldDependencies = useMemo(
    () =>
      Object.entries(fieldDependencies).reduce<Record<string, string[]>>((acc, [dep, parents]) => {
        parents.forEach((parent) => {
          if (!acc[parent]) acc[parent] = [];
          acc[parent].push(dep);
        });
        return acc;
      }, {}),
    [fieldDependencies],
  );

  const handleConfigChange = async (fieldName: string, value: string | string[]) => {
    // WHY: Recursively find all fields that (directly or transitively) depend on the
    // changed field. Clearing their values prevents stale selections from being submitted,
    // and removing them from configFields lets the re-fetch append fresh options below
    // the parent field rather than replacing the whole list.
    const findDependentFields = (name: string): string[] => {
      const direct = reverseFieldDependencies[name] ?? [];
      return [...direct, ...direct.flatMap(findDependentFields)];
    };
    const dependentFields = findDependentFields(fieldName);

    const newConfig: Record<string, string | string[]> = { ...config, [fieldName]: value };
    for (const dep of dependentFields) {
      delete newConfig[dep];
    }

    // Remove the dependent fields from the rendered list; they'll be appended after re-fetch.
    setConfigFields((prev) => prev.filter((f) => !dependentFields.includes(f.name)));
    setConfig(newConfig);

    if (dependentFields.length > 0 && selectedIntegration && selectedAction) {
      await loadConfigFields(newConfig, selectedIntegration, selectedAction, true);
    }
  };

  const isComplete = (): boolean => {
    if (!selectedIntegration || !selectedAction) return false;
    return configFields
      .filter((f) => f.required)
      .every((f) => {
        const val = config[f.name];
        if (Array.isArray(val)) return val.length > 0;
        return !!val;
      });
  };

  const handleContinue = () => {
    if (!selectedIntegration || !selectedAction || !isComplete()) return;
    onComplete(
      {
        actionId: selectedAction.id,
        credentialId: selectedIntegration.credentialId,
        provider: selectedIntegration.type,
        providerName: selectedIntegration.name,
        config,
        fieldMapping: { ...initialFieldMappingRef.current },
      },
      supportsFieldMapping,
    );
  };

  // ── Derived data ──────────────────────────────────────────────────────────

  const fetchedFieldNames = useMemo(() => new Set(configFields.map((f) => f.name)), [configFields]);

  // True while there are still dependent fields whose parents are satisfied but the fields
  // haven't been fetched yet — used to keep the Continue button hidden until all fields
  // are displayed and pre-populated.
  const hasPendingFetch = useMemo(
    () =>
      Object.entries(fieldDependencies).some(
        ([dep, parents]) =>
          !fetchedFieldNames.has(dep) && parents.length > 0 && parents.every((p) => !!config[p]),
      ),
    [fieldDependencies, fetchedFieldNames, config],
  );

  const complete = isComplete();
  const connectedIntegrations = integrations.filter(isConnected);
  const connectedTypes = new Set(connectedIntegrations.map((i) => i.type));
  // WHY: Turbine only returns already-connected integrations, so we cannot derive
  // non-connected ones from that list. Instead we use Paragon's metadata (all integrations
  // configured in the project) and subtract the connected types.
  const notConnectedIntegrations = paragonIntegrations
    .filter((i) => !connectedTypes.has(i.type))
    .sort((a, b) => a.name.localeCompare(b.name));

  const savedCredentialId = initialSettings?.credentialId;
  const isSavedIntegrationConnected =
    !!savedCredentialId && connectedIntegrations.some((i) => i.credentialId === savedCredentialId);
  const showInvalidIntegrationWarning =
    !!savedCredentialId &&
    !integrationsLoading &&
    !isSavedIntegrationConnected &&
    (!selectedIntegration || selectedIntegration.credentialId === savedCredentialId);

  const selectValue =
    selectedIntegration &&
    connectedIntegrations.some((i) => i.credentialId === selectedIntegration.credentialId)
      ? selectedIntegration.credentialId
      : '';

  // ── Render ────────────────────────────────────────────────────────────────

  // Connect panel replaces the main content in-place to avoid double back-button.
  if (showConnectPanel) {
    return (
      <div
        className="flex w-full min-w-0 flex-col gap-4 overflow-x-hidden"
        data-testid="connect-integration-panel"
      >
        <div>
          <button
            onClick={() => {
              setShowConnectPanel(false);
              setConnectError(null);
            }}
            className="mb-4 flex items-center gap-1 text-[12px] font-medium text-neutral-500 hover:text-neutral-800"
            data-testid="connect-panel-back"
          >
            ← Destination
          </button>

          <p className="mb-1 text-[10.5px] font-bold tracking-[0.06em] text-[#9898A2] uppercase">
            Available integrations
          </p>
          <p className="mb-3 text-xs leading-relaxed text-neutral-500">
            Select an integration to connect.
          </p>

          <div className="flex flex-col gap-2">
            {notConnectedIntegrations.map((integration) => (
              <button
                key={integration.type}
                onClick={() => handleConnectIntegration(integration.type)}
                data-testid={`connect-integration-option-${integration.type}`}
                className="flex w-full items-center justify-between gap-3 rounded-lg border border-neutral-200 bg-white px-3 py-2.5 text-left transition-colors hover:border-violet-400 hover:bg-violet-50"
              >
                <span className="text-[13px] font-medium text-neutral-800">{integration.name}</span>
                <span className="shrink-0 text-[11px] text-neutral-400 italic">Not connected</span>
              </button>
            ))}
          </div>

          {connectError && (
            <div className="mt-3">
              <ApiErrorBox message={connectError} />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full min-w-0 flex-col gap-4 overflow-x-hidden">
      {/* Section header */}
      <div>
        <p className="mb-1 text-[10.5px] font-bold tracking-[0.06em] text-[#9898A2] uppercase">
          Destination
        </p>
        <p className="mb-3 text-xs leading-relaxed text-neutral-500">
          Choose an integration to receive your leads.
        </p>

        {showInvalidIntegrationWarning && (
          <div
            className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5"
            role="alert"
            data-testid="saved-integration-invalid"
          >
            <p className="text-[11px] leading-relaxed text-amber-800">
              The selected integration is no longer valid. Please select a new integration.
            </p>
          </div>
        )}

        {/* Integration selector */}
        {integrationsLoading ? (
          <Spinner />
        ) : integrationsError ? (
          <ApiErrorBox message={integrationsError} />
        ) : (
          <Select value={selectValue} onValueChange={handleIntegrationChange}>
            <SelectTrigger className="w-full" data-testid="integration-select-trigger">
              <SelectValue placeholder="Select an integration…" />
            </SelectTrigger>
            <SelectContent truncate={false}>
              {connectedIntegrations.map((integration) => (
                <SelectItem
                  key={integration.credentialId}
                  value={integration.credentialId}
                  truncate={false}
                  data-testid={`integration-option-${integration.credentialId}`}
                >
                  {integration.name}
                </SelectItem>
              ))}
              {isAuthenticated && notConnectedIntegrations.length > 0 && (
                <>
                  <SelectSeparator />
                  <SelectItem
                    value={CONNECT_PANEL_VALUE}
                    truncate={false}
                    data-testid="connect-integration-panel-trigger"
                  >
                    + Connect an integration
                  </SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
        )}

        {paragonAuthError && (
          <div className="mt-2">
            <ApiErrorBox message="Unable to load available integrations. Please try refreshing the page." />
          </div>
        )}
      </div>

      {selectedIntegration &&
        connectedIntegrations.some((i) => i.credentialId === selectedIntegration.credentialId) && (
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium text-neutral-600">Action</label>
            {actionsLoading ? (
              <Spinner />
            ) : actionsError ? (
              <ApiErrorBox message={actionsError} />
            ) : (
              <Select value={selectedAction?.key ?? ''} onValueChange={handleActionChange}>
                <SelectTrigger className="w-full" data-testid="action-select-trigger">
                  <SelectValue placeholder="Select an action…" />
                </SelectTrigger>
                <SelectContent truncate={false}>
                  {actions.map((action) => (
                    <SelectItem
                      key={action.key}
                      value={action.key}
                      truncate={false}
                      data-testid={`action-option-${action.key}`}
                    >
                      {action.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        )}

      {selectedAction &&
        selectedIntegration &&
        connectedIntegrations.some((i) => i.credentialId === selectedIntegration.credentialId) && (
          <>
            {configFields.length > 0 && (
              <div className="flex flex-col gap-3" data-testid="config-fields">
                {configFields.map((field) => (
                  <ConfigField
                    key={field.name}
                    field={field}
                    value={config[field.name]}
                    onChange={(val) => void handleConfigChange(field.name, val)}
                  />
                ))}
              </div>
            )}

            {configLoading && <Spinner />}

            {/* Continue button or config error */}
            {!configLoading &&
              !hasPendingFetch &&
              (configError ? (
                <ApiErrorBox message={configError} />
              ) : (
                <button
                  onClick={handleContinue}
                  disabled={!complete}
                  data-testid="destination-continue-button"
                  className="flex w-full cursor-pointer items-center justify-center rounded-[8px] bg-violet-600 px-4 py-[9px] text-[13px] font-medium text-white transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Continue
                </button>
              ))}
          </>
        )}
    </div>
  );
};
